import { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';

/**
 * Account stats for a Legion/Watcher looking at one user.
 *
 * Everything here is derived from data that already exists — no new collections,
 * no scheduled job, no baseline pipeline. It answers "does this account look
 * odd?" today.
 *
 * Counts come from count() AGGREGATION queries against the real documents, not
 * from users.yapsCount / users.friendsCount. Those two fields are declared on
 * the User type and written by nothing, so reading them reported 0 yaps for
 * people who plainly had them. Friends live in a subcollection and yaps are a
 * query; neither keeps a counter. An aggregation does not fetch the documents
 * and bills about one read per thousand, so this stays cheap.
 *
 * The signals are HEURISTICS and are labelled as such. Every one of them has an
 * innocent explanation — a popular new account really does add a lot of friends,
 * a private person really does post nothing — so this ranks accounts for a human
 * to look at. It never concludes anything and nothing here actions an account.
 *
 * `blockedBy` is the strongest single signal available and costs nothing: it is
 * already mirrored onto every user document by the onBlockedUsersChanged
 * trigger, so it is simply the number of people who have blocked this account.
 * Ordinary users are blocked by nobody; scam accounts collect blocks fast.
 */

interface Props {
  userId: string;
  dark: boolean;
}

interface Stats {
  createdAt: Date | null;
  ageDays: number | null;
  onboardingMs: number | null;
  yaps: number;
  friends: number;
  slaps: number;
  dmsSent: number;
  dmRepeatRatio: number | null;
  likesSent: number;
  repliesSent: number;
  blockedByCount: number;
  blockedCount: number;
  photos: number;
  hasBio: boolean;
  verified: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  banned: boolean;
  banReason: string | null;
  rank: string;
}

interface Signal {
  label: string;
  detail: string;
  severity: 'high' | 'watch';
}

/** Short, human duration for how long signup took: 42s, 3m 10s, 1h 4m. */
export function formatDuration(ms: number): string {
  const secs = Math.max(0, Math.round(ms / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

const toDate = (v: any): Date | null => {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Turn the raw numbers into things worth a human's attention.
 *
 * Thresholds are starting points, not science — they are here to sort a queue,
 * and should be tuned once there is real traffic to compare against.
 */
function deriveSignals(s: Stats): Signal[] {
  const out: Signal[] = [];

  if (s.blockedByCount >= 5) {
    out.push({
      label: `Blocked by ${s.blockedByCount} people`,
      detail: 'Ordinary accounts are blocked by almost nobody. This is the strongest signal here.',
      severity: 'high',
    });
  } else if (s.blockedByCount >= 2) {
    out.push({
      label: `Blocked by ${s.blockedByCount} people`,
      detail: 'Low but not typical. Worth reading alongside the rest.',
      severity: 'watch',
    });
  }

  if (s.ageDays !== null && s.ageDays <= 1 && s.friends >= 20) {
    out.push({
      label: `${s.friends} friends in under a day`,
      detail: 'Mass-adding is how a scam account builds reach before it starts messaging.',
      severity: 'high',
    });
  } else if (s.ageDays !== null && s.ageDays <= 7 && s.friends >= 50) {
    out.push({
      label: `${s.friends} friends in ${s.ageDays} day${s.ageDays === 1 ? '' : 's'}`,
      detail: 'Fast even for a genuinely social new account.',
      severity: 'watch',
    });
  }

  if (s.friends >= 30 && s.yaps === 0) {
    out.push({
      label: `${s.friends} friends, no posts`,
      detail: 'Reach without participation. Real accounts usually post something.',
      severity: 'watch',
    });
  }

  if (s.dmRepeatRatio !== null && s.dmRepeatRatio >= 0.6) {
    out.push({
      label: `${Math.round(s.dmRepeatRatio * 100)}% of recent DMs are repeats`,
      detail: 'People write different things to different people. One message sent over and over is a campaign, not a conversation.',
      severity: 'high',
    });
  } else if (s.dmRepeatRatio !== null && s.dmRepeatRatio >= 0.35) {
    out.push({
      label: `${Math.round(s.dmRepeatRatio * 100)}% of recent DMs are repeats`,
      detail: 'Some repetition is normal (a greeting, an intro). This is above that.',
      severity: 'watch',
    });
  }

  if (s.ageDays !== null && s.ageDays <= 2 && s.dmsSent >= 30) {
    out.push({
      label: `${s.dmsSent} DMs in ${s.ageDays <= 1 ? 'under a day' : `${s.ageDays} days`}`,
      detail: 'Volume this early is the shape of a scam run rather than someone settling in.',
      severity: 'high',
    });
  }

  if (s.dmsSent >= 50 && s.yaps === 0 && s.repliesSent === 0) {
    out.push({
      label: `${s.dmsSent} DMs, nothing in public`,
      detail: 'Talks only in private, never in the open. Real accounts leave public traces.',
      severity: 'high',
    });
  }

  if (s.photos === 0) {
    out.push({
      label: 'No photos',
      detail: 'An account that finished setup normally has at least one.',
      severity: 'watch',
    });
  }

  if (s.ageDays !== null && s.ageDays <= 1 && !s.hasBio && s.photos <= 1) {
    out.push({
      label: 'Brand new, near-empty profile',
      detail: 'Weak on its own; meaningful next to anything else here.',
      severity: 'watch',
    });
  }

  return out;
}

export function AccountStatsPanel({ userId, dark }: Props) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId || userId === 'ALL_USERS') { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { getDbSafe } = await import('../services/firebaseConfig');
        const { doc, getDoc } = await import('firebase/firestore');
        const db = await getDbSafe();
        const snap = await getDoc(doc(db, 'users', userId));
        if (cancelled) return;
        if (!snap.exists()) { setError('User not found'); setLoading(false); return; }

        const d: any = snap.data();

        // Counted from the real documents, NOT from users.yapsCount or
        // users.friendsCount. Those two fields are declared on the User type and
        // never written by anything — GrowthDashboard already carries a note
        // saying so — which is why this panel reported 0 yaps for people who
        // clearly had them, and a friend count that matched nothing. Friends are
        // a SUBCOLLECTION, and yaps are a query; neither has a counter.
        //
        // count() is an aggregation: it does not fetch the documents and bills
        // about one read per thousand counted, so this stays cheap even for a
        // prolific account.
        // Counted SERVER-SIDE by getAccountStats.
        //
        // These used to be client queries, which quietly failed for the three
        // that matter most: DMs sent and DM repetition need a collection-group
        // read over `messages`, and a moderator's browser has no right to read
        // other people's chats — nor should it. The counts came back 0 and the
        // repeat ratio unavailable, which reads as a broken feature rather than
        // a denied permission. The callable runs as admin, returns only
        // aggregate numbers, and never sends message content anywhere.
        let counts: any = {
          yaps: 0, friends: 0, replies: 0, dmsSent: 0, dmRepeatRatio: null,
        };
        try {
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const res: any = await httpsCallable(getFunctions(), 'getAccountStats')({ userId });
          if (res?.data?.counts) counts = res.data.counts;
        } catch (e: any) {
          if (!cancelled) setError(e?.message || 'Could not load counts');
        }
        if (cancelled) return;

        const toMs = (v: any): number | null => {
          const dt = toDate(v);
          return dt ? dt.getTime() : null;
        };
        // Prefer the value stamped at completion; fall back to the arithmetic
        // for accounts that finished onboarding before it was recorded.
        const startedMs = toMs(d.createdAt);
        const finishedMs = toMs(d.setupCompletedAt);
        const onboardingMs = typeof d.onboardingDurationMs === 'number' && d.onboardingDurationMs > 0
          ? d.onboardingDurationMs
          : ((startedMs && finishedMs && finishedMs > startedMs) ? finishedMs - startedMs : null);
        const created = toDate(d.createdAt);
        const portfolio: any[] = Array.isArray(d.portfolio) ? d.portfolio.filter((p: any) => p && String(p).trim()) : [];

        setStats({
          createdAt: created,
          ageDays: created ? Math.floor((Date.now() - created.getTime()) / 86400000) : null,
          onboardingMs,
          yaps: counts.yaps || 0,
          friends: counts.friends || 0,
          slaps: d.slapCount || 0,
          dmsSent: counts.dmsSent || 0,
          dmRepeatRatio: typeof counts.dmRepeatRatio === 'number' ? counts.dmRepeatRatio : null,
          likesSent: Array.isArray(d.likedYapIds) ? d.likedYapIds.length : 0,
          repliesSent: counts.replies || 0,
          blockedByCount: Array.isArray(d.blockedBy) ? d.blockedBy.length : 0,
          blockedCount: Array.isArray(d.blockedUsers) ? d.blockedUsers.length : 0,
          photos: portfolio.length,
          hasBio: !!(d.bio && String(d.bio).trim()),
          verified: d.verifiedBadge === true,
          emailVerified: d.emailVerified === true,
          phoneVerified: d.phoneVerified === true,
          banned: d.banned === true || d.status === 'banned',
          banReason: d.banReason || null,
          rank: d.rank || 'user',
        });
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load stats');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="w-4 h-4 animate-spin opacity-40" />
      </div>
    );
  }
  if (error || !stats) {
    return <p className="text-xs opacity-50 py-2">{error || 'No stats available'}</p>;
  }

  const signals = deriveSignals(stats);
  const border = dark ? '#333' : '#eee';
  const muted = dark ? '#9ca3af' : '#6b7280';
  const fg = dark ? '#ffffff' : '#111111';

  const Stat = ({ label, value, warn }: { label: string; value: string | number; warn?: boolean }) => (
    <div className="rounded-lg p-2" style={{ backgroundColor: dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}>
      <p style={{ fontSize: 10, color: muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</p>
      <p style={{ fontSize: 15, fontWeight: 800, color: warn ? '#E74C3C' : fg }}>{value}</p>
    </div>
  );

  return (
    <div className="rounded-xl p-3" style={{ border: `1px solid ${border}` }}>
      <div className="flex items-center justify-between mb-2">
        <p style={{ fontSize: 11, fontWeight: 800, color: muted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Account stats
        </p>
        {stats.banned && (
          <span className="px-2 py-0.5 rounded-full" style={{ fontSize: 10, fontWeight: 800, background: 'rgba(231,76,60,0.15)', color: '#E74C3C' }}>
            SUSPENDED
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Stat label="Age" value={stats.ageDays === null ? '?' : `${stats.ageDays}d`} />
        <Stat label="Yaps" value={stats.yaps} />
        <Stat label="Friends" value={stats.friends} />
        {/* The headline number: how many people blocked THEM. */}
        <Stat label="Blocked by" value={stats.blockedByCount} warn={stats.blockedByCount >= 2} />
        <Stat label="Photos" value={stats.photos} warn={stats.photos === 0} />
        <Stat label="Slaps" value={stats.slaps} />
        <Stat label="DMs sent" value={stats.dmsSent} warn={stats.dmsSent >= 100} />
        <Stat
          label="DM repeats"
          value={stats.dmRepeatRatio === null ? '—' : `${Math.round(stats.dmRepeatRatio * 100)}%`}
          warn={stats.dmRepeatRatio !== null && stats.dmRepeatRatio >= 0.35}
        />
        <Stat label="Likes sent" value={stats.likesSent} />
        <Stat label="Replies" value={stats.repliesSent} />
        <Stat
          label="Onboarding"
          value={stats.onboardingMs === null ? '—' : formatDuration(stats.onboardingMs)}
        />
        {/* "—" here is not a bug: onboarding duration has only been recorded
            since it started being written at signup, so every account that
            finished setup before then has nothing to show. */}
      </div>

      <div className="flex items-center gap-2 mt-2" style={{ fontSize: 11, color: muted }}>
        {stats.verified
          ? <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Verified</span>
          : <span>Unverified{stats.emailVerified ? ' (email ok)' : ''}{stats.phoneVerified ? ' (phone ok)' : ''}</span>}
        <span>·</span>
        <span>{stats.rank}</span>
        {stats.createdAt && <><span>·</span><span>joined {stats.createdAt.toLocaleDateString()}</span></>}
      </div>

      {stats.banReason && (
        <p className="mt-2" style={{ fontSize: 11, color: muted }}>
          Ban reason: <span style={{ color: fg }}>{stats.banReason}</span>
        </p>
      )}

      {signals.length > 0 ? (
        <div className="mt-3 flex flex-col gap-1.5">
          <p style={{ fontSize: 10, fontWeight: 800, color: muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Irregularities ({signals.length})
          </p>
          {signals.map((sig, i) => (
            <div
              key={i}
              className="rounded-lg p-2 flex items-start gap-2"
              style={{ backgroundColor: sig.severity === 'high' ? 'rgba(231,76,60,0.12)' : 'rgba(243,156,18,0.10)' }}
            >
              <AlertTriangle
                className="w-3.5 h-3.5 shrink-0"
                style={{ marginTop: 2, color: sig.severity === 'high' ? '#E74C3C' : '#F39C12' }}
              />
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, color: fg }}>{sig.label}</p>
                <p style={{ fontSize: 11, color: muted }}>{sig.detail}</p>
              </div>
            </div>
          ))}
          {/* Said plainly, because a flag list invites being read as a verdict. */}
          <p style={{ fontSize: 10, color: muted, marginTop: 2 }}>
            Heuristics, not proof. Each has an innocent explanation; weigh them together.
          </p>
        </div>
      ) : (
        <p className="mt-3" style={{ fontSize: 11, color: muted }}>
          Nothing irregular in the numbers on this account.
        </p>
      )}
    </div>
  );
}
